import React, { useState } from 'react';

import { api, getHttpErrorStatus, logHttpError } from '../../services/api';
import SessionList from './SessionList';
import TwoFactorSetup from './TwoFactorSetup';
import type {
  AcquireSecurityOperation,
  CompleteCurrentSessionRevoke,
  ReleaseSecurityOperation,
  SecurityOperation,
} from '../../hooks/useSecurityOperationMutex';
import type {
  AcknowledgeRecoveryCodes,
  PendingRecoveryCodes,
  PublishRecoveryCodes,
} from './recoveryCodeMemory';

interface SecuritySettingsProps {
  securityOperation: SecurityOperation | null;
  acquireSecurityOperation: AcquireSecurityOperation;
  releaseSecurityOperation: ReleaseSecurityOperation;
  onCurrentSessionRevoked: CompleteCurrentSessionRevoke;
  pendingRecoveryCodes: PendingRecoveryCodes | null;
  publishRecoveryCodes: PublishRecoveryCodes;
  acknowledgeRecoveryCodes: AcknowledgeRecoveryCodes;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  onCurrentSessionRevoked,
  securityOperation,
  acquireSecurityOperation,
  releaseSecurityOperation,
  pendingRecoveryCodes,
  publishRecoveryCodes,
  acknowledgeRecoveryCodes,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordStatus, setPasswordStatus] = useState<string | null>(null);

  const changePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentPassword || newPassword.length < 8) return;
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordStatus(null);
    try {
      await api.post('/security/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setPasswordStatus('Пароль обновлён. Остальные сессии завершены.');
    } catch (passwordChangeError: unknown) {
      logHttpError('Ошибка смены пароля', passwordChangeError);
      const status = getHttpErrorStatus(passwordChangeError);
      if (status === 400) setPasswordError('Неверный текущий пароль');
      else if (status === 422) setPasswordError('Новый пароль должен содержать не менее 8 символов');
      else setPasswordError('Не удалось обновить пароль');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={changePassword} className="bg-[#15171c] p-6 rounded-2xl border border-gray-800 space-y-4" aria-labelledby="password-change-title">
        <h4 id="password-change-title" className="text-lg font-bold text-white">Смена пароля</h4>
        <div>
          <label htmlFor="current-password" className="block text-xs text-gray-500 uppercase tracking-wider font-bold mb-2">Текущий пароль</label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            aria-label="Текущий пароль"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            disabled={passwordLoading}
            className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none disabled:opacity-50"
          />
        </div>
        <div>
          <label htmlFor="new-password" className="block text-xs text-gray-500 uppercase tracking-wider font-bold mb-2">Новый пароль</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            aria-label="Новый пароль"
            minLength={8}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            disabled={passwordLoading}
            className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none disabled:opacity-50"
          />
        </div>
        {passwordError && <p role="alert" className="text-red-400 text-sm">{passwordError}</p>}
        {passwordStatus && <p role="status" className="text-green-400 text-sm">{passwordStatus}</p>}
        <button
          type="submit"
          disabled={passwordLoading || !currentPassword || newPassword.length < 8}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {passwordLoading ? 'Обновление...' : 'Обновить пароль'}
        </button>
      </form>

      <TwoFactorSetup
        securityOperation={securityOperation}
        acquireSecurityOperation={acquireSecurityOperation}
        releaseSecurityOperation={releaseSecurityOperation}
        pendingRecoveryCodes={pendingRecoveryCodes}
        publishRecoveryCodes={publishRecoveryCodes}
        acknowledgeRecoveryCodes={acknowledgeRecoveryCodes}
      />
      <SessionList
        onCurrentSessionRevoked={onCurrentSessionRevoked}
        securityOperation={securityOperation}
        acquireSecurityOperation={acquireSecurityOperation}
        releaseSecurityOperation={releaseSecurityOperation}
      />
    </div>
  );
};

export default SecuritySettings;
